/**
 * How long a socket is given to finish after we have said we are done.
 *
 * A recogniser emits its last final *after* the audio ends — that is what an
 * endpointer does, and chirp_3's trailing result has been measured at several
 * seconds. Closing straight after `stop`/`pause` threw that final away and made
 * the server's own last send fail against a closed socket, which it then recorded
 * as a lost connection rather than as the stop or pause it was.
 */
export const STOP_GRACE_MS = 3000

/**
 * Send the closing control message and let the server end the conversation.
 *
 * Messages keep flowing to `onMessage` until the server closes (it always does,
 * once it has flushed) or `graceMs` passes, whichever is first; then the socket is
 * closed from our side. Error and close handlers are detached first — this is our
 * own teardown, not a failure to report.
 *
 * Resolves when the socket has closed, by either route. A caller that must not act
 * until the server is done — the session capture, whose close request otherwise
 * races the server's last tee upload — awaits it; one that only needs the last
 * caption delivered can ignore it.
 */
export function closeGracefully(
    ws: WebSocket,
    control: string,
    onMessage: ((event: MessageEvent) => void) | null,
    graceMs = STOP_GRACE_MS
): Promise<void> {
    return new Promise((resolve) => {
        ws.onerror = null
        ws.onmessage = onMessage
        const timer = setTimeout(() => {
            ws.close()
            resolve()
        }, graceMs)
        ws.onclose = () => {
            clearTimeout(timer)
            resolve()
        }
        ws.send(control)
    })
}
